import { useState, useEffect, useRef, useCallback } from 'react';
import { DrfData, DadosDemonstrativo } from '../Pages/AnalisesDrf/types';
import { validateBasicDateFilters } from '../Utils/dateValidation';

export const useDrfData = (
    establishmentId: number | string | null, 
    dataInicio?: string, 
    dataFim?: string,
    shouldFetch: boolean = false // Controla quando deve fazer a requisição
) => {
    const [drfData, setDrfData] = useState<DrfData | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [hasInitialLoad, setHasInitialLoad] = useState(false);
    
    // Refs para controlar requisições
    const lastRequestRef = useRef<string>('');
    const requestTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const isRequestingRef = useRef(false);

    const fetchDrfData = useCallback(async (estabId: number | string, isManual: boolean = false) => {
        // Evita requisições duplicadas
        if (isRequestingRef.current) {
            console.log('Requisição já em andamento, ignorando...');
            return;
        }

        // Validação básica dos filtros de data (obrigatórios e formato válido)
        const validation = validateBasicDateFilters(dataInicio, dataFim);
        if (!validation.isValid) {
            setError(validation.error || 'Filtros de data inválidos');
            return;
        }

        // Cria chave única para a requisição
        const requestKey = `${estabId}-${dataInicio}-${dataFim}`;
        
        // Evita requisições idênticas apenas para requisições automáticas
        if (!isManual && lastRequestRef.current === requestKey) {
            console.log('Requisição automática idêntica já feita, ignorando...');
            return;
        }
        
        // Para requisições manuais, sempre permite mesmo com parâmetros idênticos
        if (isManual) {
            console.log('Requisição manual - sempre permitida');
        }

        try {
            isRequestingRef.current = true;
            setLoading(true);
            setError(null);
            lastRequestRef.current = requestKey;

            const token = document.cookie
                .split('; ')
                .find((row) => row.startsWith('authTokenMHP='))
                ?.split('=')[1];

            // Constrói a URL com o parâmetro obrigatório
            let url = `${process.env.REACT_APP_API_URL}drf/demonstrative?establishment_id=${estabId}`;
            
            // Adiciona filtros de data se fornecidos
            if (dataInicio && dataFim) {
                url += `&data_inicio=${dataInicio}&data_fim=${dataFim}`;
            }

            console.log('Fazendo requisição DRF:', url);

            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Accept': 'application/json',
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) {
                // Tratamento específico para erro 429
                if (response.status === 429) {
                    throw new Error('Muitas requisições. Aguarde alguns segundos e tente novamente.');
                }
                
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.erro || `Erro HTTP: ${response.status}`);
            }

            const data = await response.json();
            
            if (data.success) {
                setDrfData(data.data);
            } else {
                throw new Error(data.message || 'Erro ao buscar dados do DRF');
            }
        } catch (err) {
            console.error('Erro ao buscar dados do DRF:', err);
            setError(err instanceof Error ? err.message : 'Erro desconhecido');
            setDrfData(null);
            // Reset da chave em caso de erro para permitir retry
            lastRequestRef.current = '';
        } finally {
            setLoading(false);
            isRequestingRef.current = false;
        }
    }, [dataInicio, dataFim]);

    const fetchDrfStructure = async () => {
        try {
            const token = document.cookie
                .split('; ')
                .find((row) => row.startsWith('authTokenMHP='))
                ?.split('=')[1];

            const response = await fetch(
                `${process.env.REACT_APP_API_URL}drf/estrutura`,
                {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Accept': 'application/json'
                    }
                }
            );

            if (!response.ok) {
                throw new Error(`Erro HTTP: ${response.status}`);
            }

            const data = await response.json();
            return data;
        } catch (err) {
            console.error('Erro ao buscar estrutura do DRF:', err);
            throw err;
        }
    };

    useEffect(() => {
        // Limpa timeout anterior
        if (requestTimeoutRef.current) {
            clearTimeout(requestTimeoutRef.current);
        }

        // Verifica se o establishmentId é válido
        const isValidEstablishment = establishmentId && 
            (typeof establishmentId === 'number' ? establishmentId > 0 : establishmentId !== '');
        
        // Verifica se as datas são válidas (apenas se fornecidas)
        const dateValidation = (dataInicio && dataFim) ? validateBasicDateFilters(dataInicio, dataFim) : { isValid: true };
        
        // Faz a requisição apenas se:
        // 1. Dados são válidos E
        // 2. (É o carregamento inicial OU shouldFetch é true)
        if (isValidEstablishment && dateValidation.isValid && (!hasInitialLoad || shouldFetch)) {
            // Debounce de 300ms para evitar requisições muito rápidas
            requestTimeoutRef.current = setTimeout(() => {
                fetchDrfData(establishmentId, false); // false = requisição automática
                if (!hasInitialLoad) {
                    setHasInitialLoad(true);
                }
            }, 300);
        } else {
            // Se não deve fazer requisição, apenas limpa os dados se necessário
            if (!isValidEstablishment) {
                setDrfData(null);
                setError(null);
                setLoading(false);
            }
            // Se há erro de validação de data, mostra o erro
            else if (!dateValidation.isValid) {
                setError(dateValidation.error || null);
                setLoading(false);
            }
        }

        // Cleanup function
        return () => {
            if (requestTimeoutRef.current) {
                clearTimeout(requestTimeoutRef.current);
            }
        };
    }, [establishmentId, dataInicio, dataFim, shouldFetch, hasInitialLoad, fetchDrfData]);

    const manualFetch = useCallback(() => {
        const isValidEstablishment = establishmentId && 
            (typeof establishmentId === 'number' ? establishmentId > 0 : establishmentId !== '');
        const dateValidation = (dataInicio && dataFim) ? validateBasicDateFilters(dataInicio, dataFim) : { isValid: true };
        
        if(isValidEstablishment) {
            if (dateValidation.isValid) {
                // Limpa timeout anterior se existir
                if (requestTimeoutRef.current) {
                    clearTimeout(requestTimeoutRef.current);
                }
                
                // Para requisições manuais, executa imediatamente (sem debounce) e sempre permite
                fetchDrfData(establishmentId, true); // true = requisição manual
            } else {
                console.log({isValidEstablishment, coisa: dateValidation.isValid});
                setError(dateValidation.error || 'Dados inválidos para requisição');
            }
        }
    }, [establishmentId, dataInicio, dataFim, fetchDrfData]);

    return {
        drfData,
        loading,
        error,
        refetch: manualFetch,
        manualFetch, // Nova função para requisições manuais
        fetchDrfStructure
    };
};
